export type WhatsAppWebStage = 'checking' | 'starting' | 'ready' | 'preparing' | 'qr' | 'confirming';
export type WhatsAppWebPending = 'unconfirmed' | 'qr_expired' | 'disconnected';
export type WhatsAppWebMode = 'prepare' | 'connect' | 'verify';

type ConnectionOptions = {
  start: () => Promise<any>;
  getQr: () => Promise<any>;
  getStatus: () => Promise<any>;
  onQr: (qr: string) => Promise<void>;
  onStage: (stage: WhatsAppWebStage) => void;
  onPending: (reason: WhatsAppWebPending) => void;
  onConnected: () => void;
  onError: (message: string) => void;
};

// QR disponível significa pareamento pendente, como na verificação do portal.
// Também protege contra servidores antigos que retornam connected=true com QR.
function classifyConnection(status: any): 'connected' | 'disconnected' | 'unknown' {
  if (status?.hasQr === true || status?.connectionState === 'qr') return 'disconnected';
  if (status?.connected === true && status.connectionState === 'open' && status.stopReconnect !== true) return 'connected';
  if (status?.connected === false || status?.stopReconnect === true ||
    ['close', 'stopped', 'auth_failure', 'session_corrupt', 'reconnect_stopped'].includes(status?.connectionState)) return 'disconnected';
  return 'unknown';
}

// Cada etapa tem seu próprio prazo. Falha na consulta não prova desconexão,
// e verificar um vínculo existente nunca deve iniciar uma nova sessão.
export function startWhatsAppWebConnection(options: ConnectionOptions, mode: WhatsAppWebMode = 'prepare'): () => void {
  let active = true;
  let stage: WhatsAppWebStage;
  let lastQr: string | undefined;
  let statusUnavailable = false;
  let nextPoll: ReturnType<typeof setTimeout> | undefined;
  let deadline: ReturnType<typeof setTimeout> | undefined;
  const cancel = () => {
    active = false;
    if (deadline) clearTimeout(deadline);
    if (nextPoll) clearTimeout(nextPoll);
  };
  const pending = (reason: WhatsAppWebPending) => {
    if (!active) return;
    cancel();
    options.onPending(reason);
  };
  const fail = (message: string) => {
    if (!active) return;
    cancel();
    options.onError(message);
  };
  const enterStage = (next: WhatsAppWebStage, renew = false) => {
    if (stage === next && !renew) return;
    stage = next;
    if (deadline) clearTimeout(deadline);
    options.onStage(next);
    deadline = setTimeout(() => {
      if (stage === 'qr') pending('qr_expired');
      else if (stage === 'confirming' || stage === 'checking' || statusUnavailable) pending('unconfirmed');
      else if (stage === 'starting') fail('Não foi possível iniciar o WhatsApp Web. Tente novamente.');
      else fail('O QR Code ainda não ficou disponível. Tente carregá-lo novamente.');
    }, next === 'qr' ? 120000 : next === 'confirming' ? 30000 : 60000);
  };
  const isAuthError = (error: any) => error?.status === 401;

  const checkStatus = async () => {
    try {
      const response = await options.getStatus();
      if (!active) return;
      const status = response?.message;
      statusUnavailable = typeof status?.connected !== 'boolean';
      if (classifyConnection(status) === 'connected') {
        cancel();
        options.onConnected();
      } else if (mode === 'verify' && classifyConnection(status) === 'disconnected' &&
        (status.hasQr === true || status.stopReconnect === true || ['qr', 'close', 'stopped', 'auth_failure', 'session_corrupt', 'reconnect_stopped'].includes(status.connectionState))) {
        pending('disconnected');
      }
    } catch (error: any) {
      if (!active) return;
      if (isAuthError(error)) throw error;
      statusUnavailable = true;
      // Uma rota ainda não publicada pode responder 403/404. Isso não significa
      // sessão do usuário expirada nem que o WhatsApp deixou de conectar.
      if ((error?.status === 403 || error?.status === 404) && stage === 'confirming') pending('unconfirmed');
      // Falhas transitórias continuam até o prazo da etapa de confirmação.
    }
  };

  const poll = async () => {
    if (!active) return;
    try {
      if (mode === 'verify') {
        await checkStatus();
      } else {
        let qrResponse;
        let qrUnavailable = false;
        try {
          qrResponse = await options.getQr();
          qrUnavailable = !qrResponse?.message?.qr;
        } catch (error: any) {
          if (isAuthError(error)) throw error;
          qrUnavailable = `${error?.detalhe || ''} ${error?.message || ''}`.includes('QR Code ainda não gerado');
          // Erro de rede não prova que o QR foi lido ou deixou de estar disponível.
        }
        if (!active) return;
        const qr = qrResponse?.message?.qr;
        if (typeof qr === 'string' && qr) {
          const changed = lastQr !== qr;
          const showQr = changed || stage !== 'qr';
          lastQr = qr;
          enterStage('qr', changed);
          if (showQr) {
            try {
              await options.onQr(qr);
            } catch {
              fail('Não foi possível exibir o QR Code. Tente carregá-lo novamente.');
              return;
            }
          }
        } else {
          if (lastQr && qrUnavailable) enterStage('confirming');
          await checkStatus();
        }
      }
    } catch (error) {
      if (isAuthError(error)) {
        fail('Sua sessão expirou. Entre novamente para consultar a conexão do WhatsApp.');
        return;
      }
    }
    if (active) nextPoll = setTimeout(poll, 3000);
  };

  enterStage(mode === 'prepare' ? 'checking' : mode === 'verify' ? 'confirming' : 'preparing');
  const run = async (attempt = 0) => {
    if (mode === 'prepare') {
      let status;
      try {
        const response = await options.getStatus();
        status = response?.message;
      } catch (error: any) {
        if (!active) return;
        const detail = String(error?.detalhe || '');
        const transient = [0, 502, 503, 504].includes(error?.status)
          || error?.code === 'WHATSAPP_STATUS_TIMEOUT'
          || (error?.status === 400 && /timeout/i.test(detail));
        if (transient && attempt === 0) {
          // Uma falha transitória não autoriza iniciar um bot potencialmente conectado.
          // Repetir somente GET /status, uma vez, dentro do prazo da consulta inicial.
          nextPoll = setTimeout(() => { if (active) void run(attempt + 1); }, 1500);
          return;
        }
        // Instância inexistente é diferente de uma rota de status indisponível.
        if (detail.includes('Status Code: 404') && detail.includes('não encontrado')) {
          status = { connected: false };
        } else if (isAuthError(error)) {
          fail('Sua sessão expirou. Entre novamente para consultar a conexão do WhatsApp.');
          return;
        } else {
          pending('unconfirmed');
          return;
        }
      }
      if (!active) return;
      if (classifyConnection(status) === 'connected') {
        cancel();
        options.onConnected();
        return;
      }
      if (classifyConnection(status) !== 'disconnected') {
        pending('unconfirmed');
        return;
      }
      enterStage('starting');
      try {
        await options.start();
      } catch (error: any) {
        const detail = `${error?.message || ''} ${error?.detalhe || ''}`;
        if (isAuthError(error) || !detail.includes('já está inicializado')) {
          fail('Não foi possível iniciar o WhatsApp Web. Tente novamente.');
          return;
        }
      }
      if (!active) return;
      // As instruções ficam disponíveis sem consumir o prazo de leitura do QR.
      // Somente o botão Conectar WhatsApp inicia as consultas de QR.
      cancel();
      options.onStage('ready');
      return;
    }
    if (active) await poll();
  };
  void run();
  return cancel;
}
